import React, { Suspense, useContext, useEffect, useRef, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { LoadingContainer } from "go-core/components/Loading";
import RenderLimitedText from "go-core/components/RenderLimitedText";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import ClientGrantStatusLabel, { getClientGrantStatusLabelTitle } from "../../components/ClientGrantStatusLabel";
import { ClientDetailsImportContext, ClientGrantApi, ClientGrantStatus } from "../../services/types";
import ClientGrantFormModal from "./components/ClientGrantFormModal";

interface ListState {
	requests: ClientDetailsImportContext;
	resource?: Record<string, any>;
	mobileActions: MobileActionProps[];
	responseData: ClientGrantApi[];
	setResponseData: React.Dispatch<React.SetStateAction<ClientGrantApi[]>>;
	setShowCreateGrantModal: React.Dispatch<React.SetStateAction<boolean>>;
	showCreateGrantModal: boolean;
	setGrantToEdit: React.Dispatch<React.SetStateAction<ClientGrantApi | undefined>>;
	grantToEdit: ClientGrantApi | undefined;
}

interface Props {
	requests: ClientDetailsImportContext;
}

interface MatchParams {
	clientId: string;
}

const listName = "CLIENT_GRANT";
const resourceType = "CLIENT_GRANT";

const List = ({
	resource,
	requests,
	responseData,
	setResponseData,
	mobileActions,
	setShowCreateGrantModal,
	showCreateGrantModal,
	setGrantToEdit,
	grantToEdit,
}: ListState) => {
	const { t } = useTranslation();
	const segmentContext = useContext(SegmentContext);
	const { clientId } = useParams<MatchParams>();
	const listParams = useRef<Record<string, any>>({});
	const { addSuccessFlash } = useFlash();

	if (!resource) return null;
	const data = resource.read();

	const handleRevoke = async (item: ClientGrantApi) => {
		if (!item.id) return;

		await requests.revokeClientGrant(clientId, item.id);
		addSuccessFlash(t("lib:common.flash.completed"));
	};

	const handleActivate = async (item: ClientGrantApi) => {
		if (!item.id) return;

		await requests.activateClientGrant(clientId, item.id);
		addSuccessFlash(t("lib:common.flash.completed"));
	};

	const handleRemove = async (item: ClientGrantApi) => {
		if (!item.id) return;

		await requests.removeClientGrant(clientId, item.id);
		addSuccessFlash(t("lib:common.flash.removed"));
	};

	let config: ListConfig = {
		fields: [
			{
				id: "client_id",
				name: t("lib:go_component.client_details.field.client_id.title"),
			},
			{
				id: "organization_name",
				name: t("lib:go_component.client_details.field.organization_name.title"),
			},
			{
				id: "granted_scopes",
				name: "Scopes",
				render: (item: ClientGrantApi) => (
					<RenderLimitedText minWidth={200}>{item.granted_scopes}</RenderLimitedText>
				),
			},
			{
				id: "status",
				name: t("lib:common.word.status"),
				render: (item: ClientGrantApi) => <ClientGrantStatusLabel status={item.status} />,
				renderExport: (item: ClientGrantApi) => getClientGrantStatusLabelTitle(t, item.status),
			},
			{
				id: "created_at",
				name: t("lib:common.word.created_at"),
				render: (item: ClientGrantApi) => <>{FormatDate(item.created_at)}</>,
			},
			{
				id: "revoked_at",
				name: t("lib:go_component.client_details.field.revoked_at.title"),
				render: (item: ClientGrantApi) => <>{FormatDate(item.revoked_at ?? "")}</>,
			},
		],
		selectedColumns: ["client_id", "organization_name", "granted_scopes", "status", "created_at", "revoked_at"],
		actions: [
			{
				name: t("lib:common.action.edit"),
				click: (item: ClientGrantApi) => setGrantToEdit(item),
			},
			{
				name: t("lib:common.action.remove"),
				click: (item: ClientGrantApi) => handleRemove(item),
				refreshListAfter: true,
				confirmation: { use: true, message: t("lib:confirmation.message.remove") },
			},
			{
				name: t("lib:common.action.activate"),
				click: (item: ClientGrantApi) => handleActivate(item),
				refreshListAfter: true,
				confirmation: { use: true, message: t("lib:confirmation.message.activate") },
				visible: (item: ClientGrantApi) => item.status === ClientGrantStatus.REVOKED,
			},
			{
				name: t("lib:go_component.client_details.action.revoke.title"),
				click: (item: ClientGrantApi) => handleRevoke(item),
				refreshListAfter: true,
				confirmation: { use: true, message: t("lib:confirmation.message.action") },
				visible: (item: ClientGrantApi) => item.status === ClientGrantStatus.ACTIVE,
			},
		],
		segments: [
			{
				id: "all",
				name: t("lib:common.word.all"),
				slug: "all",
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			listParams.current = params;
			const res = await requests.getClientGrants(clientId, params, { cancelToken: sourceToken?.token });
			setResponseData(res);
			return res;
		},
		withoutExportActions: true,
		saveSegment: (segment: SegmentType) => {
			return segmentContext.save(listName, resourceType, segment);
		},
		numberOfStickyColumnsAtTheStart: 1,
	};
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};

	const handleRefreshList = async () => {
		if (config.fetch) {
			const res = await config.fetch(listParams.current);
			setResponseData(res);
		}
	};

	return (
		<>
			{showCreateGrantModal && (
				<ClientGrantFormModal
					show={showCreateGrantModal}
					clientId={clientId}
					grantId={undefined}
					onHide={() => setShowCreateGrantModal(false)}
					requests={requests}
					handleRefreshList={handleRefreshList}
					organizationName={undefined}
				/>
			)}
			{!!grantToEdit && (
				<ClientGrantFormModal
					show={!!grantToEdit}
					clientId={clientId}
					grantId={grantToEdit.id}
					onHide={() => setGrantToEdit(undefined)}
					requests={requests}
					handleRefreshList={handleRefreshList}
					organizationName={grantToEdit.organization_name}
				/>
			)}
			<Suspense fallback={<LoadingContainer />}>
				<ListData config={config} mobileActions={mobileActions} data={responseData} />
			</Suspense>
		</>
	);
};

const ClientDetailsClientGrantsPage = ({ requests }: Props) => {
	const isMobile = useWindowSize().isMobile;
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContext = useContext(SegmentContext);
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { clientId } = useParams<MatchParams>();
	const { t } = useTranslation();
	const [grantToEdit, setGrantToEdit] = useState<ClientGrantApi | undefined>();
	const [showCreateGrantModal, setShowCreateGrantModal] = useState(false);
	const [responseData, setResponseData] = useState<ClientGrantApi[]>([]);

	useEffect(() => {
		handleChangeTabTitle(`Client "${clientId}" grants`);
		setResource(wrapPromise(segmentContext.get(listName, resourceType)));
	}, []);

	const buttons: ButtonProps[] = [
		{
			title: t("lib:common.action.add"),
			action: () => setShowCreateGrantModal(true),
			variant: "primary",
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("lib:common.action.add"),
			action: () => setShowCreateGrantModal(true),
		},
	];

	return (
		<>
			{!isMobile && <Header title={`Client "${clientId}" grants`} buttons={buttons} />}
			<Suspense fallback={<LoadingContainer />}>
				<List
					resource={resource}
					requests={requests}
					mobileActions={mobileActions}
					responseData={responseData}
					setResponseData={setResponseData}
					showCreateGrantModal={showCreateGrantModal}
					setShowCreateGrantModal={setShowCreateGrantModal}
					setGrantToEdit={setGrantToEdit}
					grantToEdit={grantToEdit}
				/>
			</Suspense>
		</>
	);
};

export default ClientDetailsClientGrantsPage;
